/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import { connect, useDispatch, useSelector } from 'react-redux';
import { getAEMResource, getFallbackImage, getLanguageIso } from '../../selectors/generic';
import { isLoadingById } from '../../selectors/loader';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { FrequentlyBoughtTogetherComponent } from '../../data/definitions/cross-sell';
import { FrequentlyBoughtTogetherCell } from '../atoms/details/FrequentlyBoughtTogetherCell';
import { useI18N } from '../hoc/withI18N';
import { OptionalHeaderCell } from '../atoms/details/OptionalHeaderCell';
import { FrequentlyBoughtTogetherCartCell } from '../atoms/details/FrequentlyBoughtTogetherCartCell';
import { MultiProductOrderingModalId } from '../organisms/modals/MultiProductOrderingModal';
import { openModal } from '../../actions/modals';
import { isLoggedIn } from '../../data/user.model';
import { redirectToLogin } from '../../actions/auth';
import { addProductClickEvent } from '../../utils/customEventHandler';
import { selectMyPuratosAccountEmail, selectUserId } from '../../selectors/auth';
import { setProductImpressions } from '../../actions/my-puratos/myProducts.action';
import { selectBundleForMaterial } from '../../selectors/my-puratos/promotions';
import { setProductCrossSell } from '../../actions/analytics';
import { getActiveSkuNumber, getProductPageError } from '../../selectors/productPage';
import { GA4CrossSellComponentType } from '../../analytics/ga4/types/component';
import { GA4InteractionType } from '../../analytics/ga4/types/interaction';
import { getCurrencyCode } from '../../selectors/cart';
import { googleEvents } from '../../utils/googleEvents';
import { GA4LoginType } from '../../analytics/ga4/types/login';
import { GA4PageType } from '../../analytics/ga4/types/page';
import { productEventParseSelect } from '../../analytics/ga4/parsers/item';

interface IProps {
  resource?: FrequentlyBoughtTogetherComponent;
  cellOrientation: string;
  resourceLoading: boolean;
}

export const FrequentlyBoughtTogether: FunctionComponent<IProps> = props => {
  const [skus, setSkus] = useState([]);

  const dispatch = useDispatch();
  const translate = useI18N();
  const isLoading = useSelector(state => isLoadingById(state));
  const fallbackImage = useSelector(state => getFallbackImage(state));
  const activeSkuNumber = useSelector(getActiveSkuNumber);
  const hasBundles =
    useSelector(state => selectBundleForMaterial(state, activeSkuNumber))?.length > 0 || false;
  const product = props.resource?.product;
  const products = props.resource?.products;
  const userId = useSelector(selectUserId);
  const currency = useSelector(getCurrencyCode);
  const userEmail = useSelector(selectMyPuratosAccountEmail);

  const productPageError = useSelector(getProductPageError);
  const myPuratosProductAvailable =
    (products && products[0]?.aemProdId && !productPageError) || false;

  useEffect(() => {
    if (products?.length > 0 && !products.includes(product)) {
      products.unshift(product);
    }

    products && setSkus(products.map(sku => sku?.materialNumber));
    // Currently, we don't want to push impressions on product pages anymore.
    /*products && dispatch(setProductImpressions({
        products: products,
        list: GA4CrossSellComponentType.FREQUENTLY_BOUGHT_TOGETHER,
        type: GA4InteractionType.pdpFreqBoughtTogether,
    }));*/
  }, [products]);

  const changeSelection = useCallback(
    function (skuId) {
      let skuArray = [...skus];
      if (skuArray?.includes(skuId)) {
        skuArray.splice(skuArray.indexOf(skuId), 1);
      } else {
        skuArray.push(skuId);
      }

      if (!skuArray.includes(product?.materialNumber)) {
        skuArray.unshift(product?.materialNumber);
      }

      setSkus(skuArray);
    },
    [skus]
  );

  const onClickCartButton = () => {
    if (!isLoggedIn()) {
      dispatch(redirectToLogin());
      return;
    }

    const addAll = true,
      hideCartAmountAlert = true,
      showBundles = false;
    const productIds = products
      ?.map(product => (skus.includes(product.materialNumber) ? product.aemProdId : null))
      .filter(item => item !== null);

    dispatch(
      openModal(MultiProductOrderingModalId, {
        productIds,
        skus,
        hideCartAmountAlert,
        addAll,
        showBundles,
        translate,
        type: GA4InteractionType.pdpFreqBoughtTogether
      })
    );
  };

  const onClickCell = (product, position) => {
    dispatch(
      setProductCrossSell({
        ...product,
        type: GA4CrossSellComponentType.FREQUENTLY_BOUGHT_TOGETHER
      })
    );
    addProductClickEvent(
      product,
      GA4InteractionType.pdpFreqBoughtTogether,
      true,
      userId,
      currency,
      position
    );
    googleEvents.selectItem({
      user_id: userId,
      page_language: getLanguageIso(),
      email_address: isLoggedIn() ? userEmail : '',
      login_status: userId ? GA4LoginType.LOGGED_IN : GA4LoginType.GUEST,
      website_section: GA4PageType.myProducts,
      items: productEventParseSelect(product, currency, GA4InteractionType.pdpFreqBoughtTogether)
    });
  };

  if (!product || !products || products.length == 0 || !myPuratosProductAvailable) {
    return null;
  }

  return (
    <React.Fragment>
      <div className={'p-bundle-selector' + (hasBundles ? ' has-bundles' : '')}>
        <h4 className={'p-bundle-selector__header'}>{translate('Frequently bought together')}</h4>
        <div className={'p-bundle-selector__body'}>
          <div className={'p-bundle-row p-cell-orientation-vertical'}>
            <OptionalHeaderCell product={product} fallbackImage={fallbackImage} />

            {products?.map((relatedProduct, index) =>
              relatedProduct !== product ? (
                <div
                  key={relatedProduct.materialNumber}
                  className={'p-bundle-row__cell p-minicart__product'}>
                  <FrequentlyBoughtTogetherCell
                    key={'cell-' + relatedProduct.materialNumber}
                    product={relatedProduct}
                    fallbackImage={fallbackImage}
                    translate={translate}
                    changeSelection={changeSelection}
                    selected={skus.includes(relatedProduct.materialNumber)}
                    addEvent={onClickCell}
                    position={index + 1}
                  />
                </div>
              ) : null
            )}

            <FrequentlyBoughtTogetherCartCell
              translate={translate}
              color={'SECONDARY'}
              isLoading={isLoading}
              openModal={onClickCartButton}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

const mapActionsToProps = {
  openModal,
  redirectToLogin,
  setProductImpressions
};

export default connect(
  (state, ownProps: any) => ({
    resource: getAEMResource(state, ownProps.model) || {},
    resourceLoading: isLoadingById(state, ownProps.model)
  }),
  mapActionsToProps
)(FrequentlyBoughtTogether);
